/** Canonical JSON rejects non-data values before an untrusted save is read. */
export function canonicalJSON(value: unknown, omitChecksum = false): string | null {
    const active = new Set<object>();
    let characters = 0;
    const encode = (node: unknown, depth: number): string | null => {
        if (depth > 32 || characters > 1024 * 1024) return null;
        if (node === null || typeof node === 'boolean' || typeof node === 'string' || typeof node === 'number' && Number.isFinite(node)) {
            const text = JSON.stringify(node); characters += text.length; return text;
        }
        if (!node || typeof node !== 'object' || active.has(node)) return null;
        const prototype = Object.getPrototypeOf(node);
        if (!Array.isArray(node) && prototype !== Object.prototype && prototype !== null) return null;
        const descriptors = Object.getOwnPropertyDescriptors(node);
        if (Object.values(descriptors).some(property => property.get || property.set)) return null;
        active.add(node);
        const pieces: string[] = [];
        if (Array.isArray(node)) {
            for (let index = 0; index < node.length; index++) {
                const item = encode(node[index], depth + 1);
                if (item === null) { active.delete(node); return null; }
                pieces.push(item);
            }
        } else {
            for (const key of Object.keys(node).sort()) {
                if (depth === 0 && omitChecksum && key === 'checksum') continue;
                const item = encode(descriptors[key].value, depth + 1);
                if (item === null) { active.delete(node); return null; }
                pieces.push(JSON.stringify(key) + ':' + item);
                characters += key.length;
            }
        }
        active.delete(node);
        return (Array.isArray(node) ? '[' : '{') + pieces.join(',') + (Array.isArray(node) ? ']' : '}');
    };
    try {
        const result = encode(value, 0);
        return result !== null && new TextEncoder().encode(result).length <= 1024 * 1024 ? result : null;
    } catch {
        // Untrusted proxies may throw during reflection; validation still returns a value.
        return null;
    }
}

export function checksumJSON(value: unknown): string | null {
    const json = canonicalJSON(value, true);
    if (json === null) return null;
    let hash = 0x811c9dc5;
    for (let index = 0; index < json.length; index++) hash = Math.imul(hash ^ json.charCodeAt(index), 0x01000193) >>> 0;
    return hash.toString(16).padStart(8, '0');
}
